/*********************************************************************
* © Copyright IBM Corp. 2024
*********************************************************************/
import React, {useState, useCallback} from 'react'
import {Input} from '@targetprocess/input'
import {SearchIcon} from '@targetprocess/icons'
import styles from '../style/index.css'
import classNames from 'classnames'
import {debounce} from 'underscore'
import {FormControlGroup} from '@targetprocess/form-control-group'
import {Button} from '@targetprocess/button'
import {PayloadRow} from './PayloadRow'
import {moveKeyToTheEnd} from '../helpers/keyOrder'
import intl from '../utils/intl/intl'

export interface SearchHeaderProps {
  query?: string
  onQueryChange?: (query: string) => void
}

export const SearchHeader = ({query: queryProp, onQueryChange}: SearchHeaderProps) => {
  const [query, setQuery] = useState(queryProp ?? '')
  const change = useCallback(
    debounce((text: string) => {
      onQueryChange && onQueryChange(text)
    }, 500),
    []
  )
  return (
    <div className={styles['log__search-container']}>
      <Input
        text={query ?? ''}
        kind="default"
        leftElement={<SearchIcon color="#000" />}
        placeholder={intl.formatMessage('Filter Message')}
        onTextChanged={(newText) => {
          setQuery(newText)
          change(newText)
        }}
      />
    </div>
  )
}

export interface MessageRowProps {
  showDetails?: boolean
  item: {
    id: string
    message?: string
    eventType?: string
    area?: string
    processName?: string
    discriminator1?: string
    discriminator2?: string
    payload?: Record<string, any>
  }
}

type PayloadViewMode = 'table' | 'json'

export const MessageDetailsTableView: React.FC<{properties: Record<string, any>}> = ({
  properties
}) => {
  const propertyKeys = moveKeyToTheEnd('originatorId', Object.keys(properties))

  return (
    <>
      {propertyKeys.map((key) => {
        // nosemgrep: eslint.detect-object-injection
        return <PayloadRow key={key} payloadKey={key} value={properties[key]} />
      })}
    </>
  )
}

export const MessageDetailsJsonView: React.FC<{properties: Record<string, any>}> = ({
  properties
}) => (
  <div className={styles['log__cell-item']}>
    <pre>{JSON.stringify(properties, null, '\t')}</pre>
  </div>
)

export const MessageDetails: React.FC<{
  showDetails: boolean
  tableView: React.ReactChild
  jsonView: React.ReactChild
}> = ({showDetails, tableView, jsonView}) => {
  const [viewMode, setViewMode] = useState('table' as PayloadViewMode)
  const getClickHandler = useCallback(
    (currentValue: PayloadViewMode, newValue: PayloadViewMode) => () => {
      newValue !== currentValue && setViewMode(newValue)
    },
    []
  )
  return showDetails ? (
    <div
      className={classNames(styles['log__cell-item__value-details'])}
      onClick={(e) => e.stopPropagation()}
    >
      <div onMouseUp={(e) => e.stopPropagation()}>
        <FormControlGroup>
          <Button checked={viewMode === 'table'} onClick={getClickHandler(viewMode, 'table')}>
            {intl.formatMessage('Table')}
          </Button>
          <Button onClick={getClickHandler(viewMode, 'json')} checked={viewMode === 'json'}>
            {intl.formatMessage('JSON')}
          </Button>
        </FormControlGroup>
      </div>

      {viewMode === 'table' ? tableView : jsonView}
    </div>
  ) : null
}

export const MessageRow: React.FC<MessageRowProps & {messageDetailsView?: React.ReactChild}> = ({
  item,
  showDetails,
  messageDetailsView
}) => (
  <div
    className={classNames(
      styles['log__cell'],
      styles[showDetails ? 'log__cell-details-shown' : '']
    )}
  >
    <div
      className={classNames(styles['log__cell-item'], styles['log__cell-item__value'])}
      title={item.message}
    >
      {item.message}
    </div>
    {showDetails
      ? messageDetailsView || (
          <MessageDetails
            showDetails
            tableView={<MessageDetailsTableView properties={item.payload || {}} />}
            jsonView={<MessageDetailsJsonView properties={item.payload || {}} />}
          />
        )
      : null}
  </div>
)
